import * as Sentry from '@sentry/react'
import type { AppError, ErrorSeverity } from '../types/errors'

type ErrorMetadata = {
  context?: string
  status?: number
  code?: string
  sessionId?: string
  severity?: ErrorSeverity
  [key: string]: unknown
}

type PostgrestErrorPayload = {
  code?: string
  message?: string
}

export function normalizeError(error: unknown, metadata: ErrorMetadata = {}): AppError {
  if (isAppError(error)) {
    return {
      ...error,
      context: metadata.context ?? error.context,
      severity: metadata.severity ?? error.severity,
      status: metadata.status ?? error.status,
      code: metadata.code ?? error.code,
    }
  }

  const postgrestPayload = extractPostgrestError(error)
  const baseError = error instanceof Error
    ? error
    : new Error(postgrestPayload.message ?? 'Unknown application error')

  const appError: AppError = Object.assign(baseError, {
    timestamp: new Date().toISOString(),
    severity: metadata.severity ?? inferSeverity(metadata.status),
    context: metadata.context ?? 'unknown',
    code: metadata.code ?? postgrestPayload.code,
    status: metadata.status,
    originalError: error,
  })

  return appError
}

export function logErrorToService(error: unknown, metadata: ErrorMetadata = {}): AppError {
  const normalizedError = normalizeError(error, metadata)
  const sentryTags: Record<string, string> = {
    context: normalizedError.context,
    severity: normalizedError.severity,
  }

  if (normalizedError.code) {
    sentryTags.code = normalizedError.code
  }

  if (typeof metadata.sessionId === 'string' && metadata.sessionId.length > 0) {
    sentryTags.session_id = metadata.sessionId
  }

  console.error('[error]', {
    message: normalizedError.message,
    code: normalizedError.code,
    sessionId: metadata.sessionId,
    context: normalizedError.context,
    severity: normalizedError.severity,
    status: normalizedError.status,
    timestamp: normalizedError.timestamp,
  })

  Sentry.captureException(normalizedError, {
    tags: sentryTags,
    extra: {
      status: normalizedError.status,
      timestamp: normalizedError.timestamp,
      metadata,
    },
  })

  return normalizedError
}

function extractPostgrestError(error: unknown): PostgrestErrorPayload {
  if (!error || typeof error !== 'object') {
    return {}
  }

  const candidate = error as PostgrestErrorPayload

  return {
    code: typeof candidate.code === 'string' ? candidate.code : undefined,
    message: typeof candidate.message === 'string' ? candidate.message : undefined,
  }
}

function inferSeverity(status?: number): ErrorSeverity {
  if (!status) return 'medium'
  if (status >= 500) return 'high'
  if (status >= 400) return 'medium'
  return 'low'
}

function isAppError(error: unknown): error is AppError {
  return Boolean(
    error
    && typeof error === 'object'
    && 'timestamp' in error
    && 'severity' in error
    && 'context' in error,
  )
}

const CHUNK_LOAD_PATTERNS = [
  /Failed to fetch dynamically imported module/,
  /Loading chunk \d+ failed/,
  /ChunkLoadError/,
]

export function isChunkLoadError(error: unknown): boolean {
  if (error instanceof Error) {
    return CHUNK_LOAD_PATTERNS.some(p => p.test(error.message))
  }
  if (typeof error === 'string') {
    return CHUNK_LOAD_PATTERNS.some(p => p.test(error))
  }
  return false
}
